/**
 * Text-to-speech utilities using Web Speech API
 */

let speechSynthesis: SpeechSynthesis | null = null;
let currentUtterance: SpeechSynthesisUtterance | null = null;

/**
 * Initialize speech synthesis
 */
export function initTextToSpeech(): boolean {
  if (typeof window === 'undefined') return false;

  if ('speechSynthesis' in window) {
    speechSynthesis = window.speechSynthesis;
    return true;
  }

  return false;
}

/**
 * Check if text-to-speech is available
 */
export function isTextToSpeechAvailable(): boolean {
  return speechSynthesis !== null;
}

/**
 * Speak text using text-to-speech
 */
export function speak(text: string, options?: {
  rate?: number;
  pitch?: number;
  volume?: number;
  onEnd?: () => void;
  onError?: (error: Error) => void;
}): void {
  if (!speechSynthesis || !text) return;

  // Cancel any ongoing speech
  stopSpeaking();

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.rate = options?.rate ?? 1.0;
  utterance.pitch = options?.pitch ?? 1.0;
  utterance.volume = options?.volume ?? 1.0;

  if (options?.onEnd) {
    utterance.onend = options.onEnd;
  }

  if (options?.onError) {
    utterance.onerror = (event) => {
      options.onError?.(new Error(event.error));
    };
  }

  currentUtterance = utterance;
  speechSynthesis.speak(utterance);
}

/**
 * Stop current speech
 */
export function stopSpeaking(): void {
  if (speechSynthesis) {
    speechSynthesis.cancel();
    currentUtterance = null;
  }
}

/**
 * Pause current speech
 */
export function pauseSpeaking(): void {
  if (speechSynthesis && speechSynthesis.speaking) {
    speechSynthesis.pause();
  }
}

/**
 * Resume paused speech
 */
export function resumeSpeaking(): void {
  if (speechSynthesis && speechSynthesis.paused) {
    speechSynthesis.resume();
  }
}

/**
 * Check if currently speaking
 */
export function isSpeaking(): boolean {
  return speechSynthesis?.speaking ?? false;
}

/**
 * Get available voices
 */
export function getVoices(): SpeechSynthesisVoice[] {
  if (!speechSynthesis) return [];
  return speechSynthesis.getVoices();
}

/**
 * Set preferred voice
 */
export function setVoice(voice: SpeechSynthesisVoice | null): void {
  if (currentUtterance && voice) {
    currentUtterance.voice = voice;
  }
}
